import { memo, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import * as api from '@/features/canvas/api';
import { CanvasElement, RemoteLock } from '@/features/canvas/types';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import ItemCard from './item-card';
import SearchCard from './search-card';

// Where the text caret should land when the NEXT edit opens: a character offset (double-clicked ON text),
// 'end' (double-clicked on the element's whitespace), or null (edit opened another way → select all, the
// default). Captured on the double-click BEFORE the textarea mounts (using the still-present content text
// node, where caretRangeFromPoint is reliable), then consumed by EditableText on focus.
let pendingCaret: number | 'end' | null = null;

/** Character offset in the text under a viewport point, or null when the point isn't over a text glyph
 *  (e.g. the element's blank area / padding). */
function caretOffsetAtPoint(x: number, y: number): number | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  let offset = 0;
  if (doc.caretPositionFromPoint) {
    const cp = doc.caretPositionFromPoint(x, y);
    if (cp) {
      node = cp.offsetNode;
      offset = cp.offset;
    }
  } else if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) {
      node = r.startContainer;
      offset = r.startOffset;
    }
  }
  return node && node.nodeType === Node.TEXT_NODE ? offset : null;
}

/** Overlay shown when a PEER has this element selected/locked: a tinted veil + inset ring in their colour
 *  and a small badge. The element itself is made non-interactive (pointer-events: none) by the caller. */
function LockChrome({ lock }: { lock: RemoteLock }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-[2] overflow-visible rounded-[2px]" style={{ background: `${lock.color}26`, boxShadow: `inset 0 0 0 2px ${lock.color}` }}>
      <div className="absolute left-0 top-0 -translate-y-full whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm" style={{ background: lock.color }}>
        {lock.name || 'Someone'} {lock.editing ? 'is editing' : 'selected'}
      </div>
    </div>
  );
}

const STICKY_DEFAULT = '#fdd835'; // warm sticky yellow
const SHAPE_DEFAULT = '#4dabf7';

/** Relative luminance (sRGB) of a #hex colour, for picking readable text on any sticky/shape fill. */
function relLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(s.slice(0, 2), 16) / 255;
  const g = parseInt(s.slice(2, 4), 16) / 255;
  const b = parseInt(s.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Dark ink on light fills, light ink on dark fills — keeps text legible when a sticky is recoloured. */
function inkFor(bg: string): string {
  try {
    return relLuminance(bg) > 0.45 ? '#1a1a1a' : '#f7f7f5';
  } catch {
    return '#1a1a1a';
  }
}

// Decoded image object-URLs cached by their STORAGE KEY (not element id). A duplicated image shares
// its source's storage_key but gets a NEW element id whose row may not be persisted the instant its
// <img> mounts — so keying on storage_key lets the copy reuse the source's already-loaded blob and
// show immediately (no fetch, no "unavailable" race), and de-dupes repeats of the same image. The
// cache is session-scoped, bounded by the number of DISTINCT images (so the URLs aren't revoked).
const imageBlobUrls = new Map<string, string>();

/** An image element: bytes are served through the auth'd stream endpoint, shown via an object URL. */
function CanvasImage({ el }: { el: CanvasElement }) {
  const externalSrc = el.style?.src;
  const storageKey = el.style?.storage_key;
  const [url, setUrl] = useState<string | null>(externalSrc ?? (storageKey ? imageBlobUrls.get(storageKey) ?? null : null));
  const [error, setError] = useState(false);
  useEffect(() => {
    if (externalSrc) {
      setUrl(externalSrc); // pasted/dropped link — load directly, no storage stream
      return undefined;
    }
    if (storageKey) {
      const hit = imageBlobUrls.get(storageKey); // a duplicate reuses its source's already-loaded blob
      if (hit) {
        setUrl(hit);
        setError(false);
        return undefined;
      }
    }
    let cancelled = false;
    let tries = 0;
    setError(false);
    const load = () => {
      api
        .fetchElementImageBlob(el.id)
        .then((blob) => {
          if (cancelled) return;
          const obj = URL.createObjectURL(blob);
          if (storageKey) imageBlobUrls.set(storageKey, obj); // shared by every element on this image
          setUrl(obj);
        })
        .catch(() => {
          if (cancelled) return;
          // A freshly-duplicated element may not be persisted server-side yet — retry a few times.
          if (tries++ < 4) window.setTimeout(load, 250 * tries);
          else setError(true);
        });
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [el.id, externalSrc, storageKey]);
  if (error)
    return <div className="flex h-full w-full items-center justify-center bg-surface-inset text-[12px] text-fg-faint">Image unavailable</div>;
  if (!url)
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-inset">
        <Spinner className="h-5 w-5 text-fg-faint" />
      </div>
    );
  return <img src={url} alt="" draggable={false} className="h-full w-full select-none object-contain" />;
}

type VAlign = 'top' | 'middle' | 'bottom';
type HAlign = 'left' | 'center' | 'right';

interface EditableTextProps {
  editing: boolean;
  content: string | null;
  fontSize: number;
  bold?: boolean;
  hAlign: HAlign;
  vAlign: VAlign;
  /** Text colour; omit to inherit from the container (sticky/shape set an ink colour on the wrapper). */
  color?: string;
  placeholder: string;
  placeholderClassName?: string;
  padding?: number | string;
  onCommit: (text: string) => void;
  onEditEnd: () => void;
  /** Reports the pixel height the content needs (text + padding) so the parent can grow the element
   *  instead of scrolling. Fired while editing (on mount + every keystroke). */
  onAutoHeight?: (px: number) => void;
}

/**
 * Inline text for sticky / text / shape. The editor is an auto-height textarea sized to its content
 * and positioned by the flex container's `justify` — so the caret sits exactly where the rendered text
 * does (no top-when-editing / middle-when-idle jump), and vertical alignment is a single control.
 *
 * The draft commits on ANY exit — blur, Escape, click-away, or unmount. React does not fire blur on
 * unmount, so the effect cleanup is what saves the text when a background click removes the textarea
 * (this was the cause of content being cleared); a ref-guard makes the double path commit only once.
 */
function EditableText({ editing, content, fontSize, bold, hAlign, vAlign, color, placeholder, placeholderClassName, padding, onCommit, onEditEnd, onAutoHeight }: EditableTextProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState(content ?? '');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const contentRef = useRef(content ?? '');
  contentRef.current = content ?? '';
  const committedRef = useRef(false);

  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    if (draftRef.current !== contentRef.current) onCommit(draftRef.current);
    onEditEnd();
  }, [onCommit, onEditEnd]);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const autosize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    if (onAutoHeight) {
      // Auto-grow element (sticky): the textarea always shows all content and the element grows to
      // match (no cap, no scrollbar) — see the onAutoHeight report below.
      ta.style.height = `${ta.scrollHeight}px`;
      const outer = outerRef.current;
      if (outer) {
        const cs = getComputedStyle(outer);
        onAutoHeight(ta.scrollHeight + (parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0')));
      }
    } else {
      // Fixed-height element (text/shape): cap to the container and scroll the overflow, as before.
      const max = ta.parentElement?.clientHeight ?? Number.POSITIVE_INFINITY;
      ta.style.height = `${Math.min(ta.scrollHeight, max)}px`;
    }
  }, [onAutoHeight]);
  const autosizeRef = useRef(autosize);
  autosizeRef.current = autosize;

  // LAYOUT effect so the COMMIT (in the cleanup) runs BEFORE paint: clicking out sets editingId=null, which
  // re-renders this element non-editing showing the OLD content; a passive effect would then commit the new
  // text only after that frame painted → a one-frame flash of the old text. Committing in the layout-effect
  // cleanup applies the new content before the browser paints, so the old text is never shown.
  useLayoutEffect(() => {
    if (!editing) return undefined;
    committedRef.current = false;
    setDraft(contentRef.current);
    const ta = taRef.current;
    if (ta) {
      ta.focus();
      // Double-click placed the caret where the mouse was (or at the end for a blank spot); any other
      // way of entering edit mode selects all. `ta.value` already equals the content here (same as when
      // the previous code called ta.select()).
      if (pendingCaret === 'end') ta.setSelectionRange(ta.value.length, ta.value.length);
      else if (pendingCaret != null) {
        const pos = Math.min(pendingCaret, ta.value.length);
        ta.setSelectionRange(pos, pos);
      } else ta.select();
      pendingCaret = null;
    }
    // Autosize through a ref so this effect depends ONLY on `editing`. `autosize` gets a new identity on
    // every render (the parent hands EditableText a fresh onAutoHeight closure each time), and a sticky's
    // autosize grows the element via a state update → re-render. Depending on `autosize` re-ran this effect
    // on every render, resetting the draft + re-selecting the text mid-typing and spiralling into a freeze.
    const raf = requestAnimationFrame(() => autosizeRef.current());
    return () => {
      cancelAnimationFrame(raf);
      commitRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const justify = vAlign === 'top' ? 'flex-start' : vAlign === 'bottom' ? 'flex-end' : 'center';
  const shared: React.CSSProperties = { fontSize, lineHeight: 1.25, textAlign: hAlign, fontWeight: bold ? 700 : undefined, color: color ?? 'inherit', width: '100%' };

  return (
    <div ref={outerRef} className="flex h-full w-full flex-col overflow-hidden" style={{ justifyContent: justify, padding }}>
      {editing ? (
        <textarea
          ref={taRef}
          value={draft}
          rows={1}
          onChange={(e) => {
            setDraft(e.target.value);
            autosize();
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              commit();
            }
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn('resize-none border-0 bg-transparent p-0 outline-none', onAutoHeight ? 'overflow-hidden' : 'overflow-y-auto')}
          style={shared}
        />
      ) : content ? (
        <div className="select-none whitespace-pre-wrap break-words" style={shared}>
          {content}
        </div>
      ) : placeholder ? (
        <div className={cn('select-none', placeholderClassName)} style={{ ...shared, color: undefined }}>
          {placeholder}
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  el: CanvasElement;
  selected: boolean;
  editing: boolean;
  onPointerDown: (e: ReactPointerEvent, id: string) => void;
  onDoubleClick: (id: string) => void;
  onContentCommit: (id: string, text: string) => void;
  onEditEnd: () => void;
  /** Grow the element (a sticky) to fit its content height instead of scrolling it. */
  onAutoHeight?: (id: string, px: number) => void;
  /** Set when a PEER has this element locked (selected/editing) — shade it + make it non-interactive. */
  lock?: RemoteLock;
  /** A search-card reporting the boards its current results span (cached to style.board_ids for fan-out). */
  onSetSearchBoards?: (id: string, boardIds: string[]) => void;
}

/**
 * One DOM element rendered in world space (inside the scaled canvas container). Sticky/text/shape
 * support inline editing (see EditableText); images stream their bytes. All kinds forward pointerdown
 * so the canvas can start a move/select gesture.
 */
function ElementView({ el, selected, editing, onPointerDown, onDoubleClick, onContentCommit, onEditEnd, onAutoHeight, lock, onSetSearchBoards }: Props) {
  const fontSize = el.style?.fontSize ?? (el.kind === 'text' ? 24 : 16);
  const hAlign = el.style?.textAlign;
  const vAlign = el.style?.vAlign;
  const bold = el.style?.bold;
  const base: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: el.w,
    height: el.h,
    pointerEvents: lock ? 'none' : undefined, // a peer's locked element is non-interactive here
    // Position via transform (NOT left/top) so a drag is a COMPOSITOR move — no per-frame layout + repaint
    // of the box-shadow / gradient sheen, which is what tanks drag fps (badly on Windows/Chrome). The
    // dragged nodes get `will-change: transform` set IMPERATIVELY only for the duration of the drag (see
    // index.tsx) — NOT tied to selection, so a marquee that selects many elements doesn't promote a GPU
    // layer per element (which itself tanked marquee fps).
    transform: `translate(${el.x}px, ${el.y}px)${el.rotation ? ` rotate(${el.rotation}deg)` : ''}`,
    transformOrigin: 'center center',
    touchAction: 'none',
  };
  const commitText = (t: string) => onContentCommit(el.id, t);
  // Double-click to edit: remember where in the text the mouse landed (or 'end' for a blank area) so the
  // caret opens there instead of the whole content being selected.
  const startEdit = (e: ReactMouseEvent) => {
    const off = caretOffsetAtPoint(e.clientX, e.clientY);
    pendingCaret = off != null ? off : 'end';
    onDoubleClick(el.id);
  };

  // Sticky note: warm paper with a soft lift shadow and a subtle sheen, centred text, ink auto-contrasts.
  if (el.kind === 'sticky') {
    const bg = el.color ?? STICKY_DEFAULT;
    return (
      <div data-elid={el.id} style={base} onPointerDown={(e) => onPointerDown(e, el.id)} onDoubleClick={startEdit} className={cn('group')}>
        <div
          className="relative h-full w-full overflow-hidden"
          style={{
            background: bg,
            borderRadius: 2,
            color: inkFor(bg),
            boxShadow: '0 10px 22px -10px rgba(0,0,0,0.32), 0 2px 5px rgba(0,0,0,0.16), 0 1px 0 rgba(0,0,0,0.05)',
          }}
        >
          {/* paper sheen: a brighter top-left falling to a faint shadow at the bottom — reads as a note on any colour. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'linear-gradient(157deg, rgba(255,255,255,0.30), rgba(255,255,255,0) 42%, rgba(0,0,0,0.06) 100%)' }}
          />
          <EditableText
            editing={editing}
            content={el.content}
            fontSize={fontSize}
            bold={bold}
            hAlign={hAlign ?? 'center'}
            vAlign={vAlign ?? 'middle'}
            placeholder="Type…"
            placeholderClassName="opacity-30"
            padding="9%"
            onCommit={commitText}
            onEditEnd={onEditEnd}
            onAutoHeight={onAutoHeight ? (px) => onAutoHeight(el.id, px) : undefined}
          />
        </div>
        {lock ? <LockChrome lock={lock} /> : null}
      </div>
    );
  }

  // Free text: transparent, theme-coloured (or its own colour), top-left aligned by default.
  if (el.kind === 'text') {
    return (
      <div data-elid={el.id} style={base} onPointerDown={(e) => onPointerDown(e, el.id)} onDoubleClick={startEdit} className={cn('group text-fg')}>
        <EditableText
          editing={editing}
          content={el.content}
          fontSize={fontSize}
          bold={bold}
          hAlign={hAlign ?? 'center'}
          vAlign={vAlign ?? 'middle'}
          color={el.color ?? undefined}
          placeholder="Text"
          placeholderClassName="text-fg-ghost"
          onCommit={commitText}
          onEditEnd={onEditEnd}
        />
        {lock ? <LockChrome lock={lock} /> : null}
      </div>
    );
  }

  if (el.kind === 'image') {
    return (
      <div data-elid={el.id} style={base} onPointerDown={(e) => onPointerDown(e, el.id)} className={cn('overflow-hidden rounded-[2px]')}>
        <CanvasImage el={el} />
        {lock ? <LockChrome lock={lock} /> : null}
      </div>
    );
  }

  // Board item card (SBB-13): a live backlog item rendered on the canvas. Its own interactive controls
  // (title input, status/assignee/priority pickers, subtree toggles) stop pointer propagation so they
  // don't start a canvas move; the card body still forwards pointerdown so the element itself drags.
  if (el.kind === 'item') {
    // Content-driven height so the card never clips or scrolls: `base` fixes the width; height is auto
    // and the measured height is reported via onAutoHeight so the selection box tracks the real card.
    return (
      <div data-elid={el.id} style={{ ...base, height: 'auto' }} onPointerDown={(e) => onPointerDown(e, el.id)} className="group">
        {/* Two-step interaction: the card's controls (pickers, title, subtree rows, hover) go live ONLY
            once the card is selected. Until then the content is pointer-transparent, so pointerdowns fall
            through to this root and just select/drag — panning or stray clicks never fire a control. */}
        <div style={{ pointerEvents: selected ? 'auto' : 'none' }}>
          <ItemCard element={el} onAutoHeight={onAutoHeight ? (px) => onAutoHeight(el.id, px) : undefined} />
        </div>
        {lock ? <LockChrome lock={lock} /> : null}
      </div>
    );
  }

  // Board search results card (SBB-14): a live table for a saved SparkQL query.
  if (el.kind === 'search') {
    return (
      <div data-elid={el.id} style={{ ...base, height: 'auto' }} onPointerDown={(e) => onPointerDown(e, el.id)} className="group">
        {/* Interactive only once selected (see item card above). */}
        <div style={{ pointerEvents: selected ? 'auto' : 'none' }}>
          <SearchCard
            element={el}
            onAutoHeight={onAutoHeight ? (px) => onAutoHeight(el.id, px) : undefined}
            onSetBoards={onSetSearchBoards ? (ids) => onSetSearchBoards(el.id, ids) : undefined}
          />
        </div>
        {lock ? <LockChrome lock={lock} /> : null}
      </div>
    );
  }

  // Shapes: rectangle / ellipse / diamond / triangle.
  const variant = el.style?.shape ?? 'rectangle';
  const fill = el.color ?? SHAPE_DEFAULT;
  const clip =
    variant === 'diamond'
      ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
      : variant === 'triangle'
        ? 'polygon(50% 0%, 100% 100%, 0% 100%)'
        : undefined;
  return (
    <div data-elid={el.id} style={base} onPointerDown={(e) => onPointerDown(e, el.id)} onDoubleClick={startEdit} className={cn('group')}>
      <div
        className="h-full w-full overflow-hidden"
        style={{
          background: fill,
          color: inkFor(fill),
          borderRadius: variant === 'ellipse' ? '50%' : variant === 'rectangle' ? 6 : undefined,
          clipPath: clip,
        }}
      >
        <EditableText
          editing={editing}
          content={el.content}
          fontSize={fontSize}
          bold={bold}
          hAlign={hAlign ?? 'center'}
          vAlign={vAlign ?? 'middle'}
          placeholder=""
          padding={8}
          onCommit={commitText}
          onEditEnd={onEditEnd}
        />
      </div>
      {lock ? <LockChrome lock={lock} /> : null}
    </div>
  );
}

// Memoized: a camera pan/zoom re-renders the controller but not each element (props unchanged).
export default memo(ElementView);
